import { createDemo } from '@mui/internal-core-docs/utils/createDemo';
import ExtensibleThemes from './ExtensibleThemes';
import ClientProvider from './client';
export default createDemo(import.meta.url, ExtensibleThemes, { ClientProvider, precompute: {
  "Default": {
    "url": "file:///opt/build/repo/docs/data/material/guides/building-extensible-themes/demos/extensible-themes/ExtensibleThemes.tsx",
    "fileName": "ExtensibleThemes.tsx",
    "language": "tsx",
    "source": {
      "type": "root",
      "children": [
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "import Box from '@mui/material/Box';\nimport Button from '@mui/material/Button';\nimport { ThemeProvider, createTheme, type ThemeOptions } from '@mui/material/styles';\n\n/**\n * Branded theme: you might want to export this as a separate file\n */\nconst brandedTokens: ThemeOptions = {\n  palette: {\n    primary: {\n      main: '#000000',\n    },\n    secondary: {\n      main: 'rgb(229, 229, 234)',\n    },\n  },\n  shape: {\n    borderRadius: 4,\n  },\n  typography: {\n    fontFamily:\n      'var(--font-primary, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif)',\n  },\n  shadows: [\n    'none',\n    '0 1px 2px 0 rgb(0 0 0 / 0.05)',\n    '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',\n    '0 2px 4px 0 rgb(0 0 0 / 0.06)',\n    '0 2px 4px -1px rgb(0 0 0 / 0.06), 0 1px 2px -1px rgb(0 0 0 / 0.04)',\n    '0 3px 5px -1px rgb(0 0 0 / 0.07), 0 1px 3px -1px rgb(0 0 0 / 0.05)',\n    '0 4px 6px -1px rgb(0 0 0 / 0.07), 0 2px 4px -1px rgb(0 0 0 / 0.05)',\n    '0 5px 8px -2px rgb(0 0 0 / 0.08), 0 2px 4px -1px rgb(0 0 0 / 0.05)',\n    '0 6px 10px -2px rgb(0 0 0 / 0.08), 0 3px 5px -2px rgb(0 0 0 / 0.06)',\n    '0 8px 12px -3px rgb(0 0 0 / 0.09), 0 3px 6px -2px rgb(0 0 0 / 0.06)',\n    '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 7px -3px rgb(0 0 0 / 0.07)',\n    '0 12px 18px -4px rgb(0 0 0 / 0.11), 0 5px 9px -3px rgb(0 0 0 / 0.08)',\n    '0 15px 22px -4px rgb(0 0 0 / 0.12), 0 6px 11px -4px rgb(0 0 0 / 0.09)',\n    '0 18px 28px -5px rgb(0 0 0 / 0.13), 0 7px 13px -4px rgb(0 0 0 / 0.1)',\n    '0 22px 34px -6px rgb(0 0 0 / 0.14), 0 8px 16px -5px rgb(0 0 0 / 0.11)',\n    '0 26px 40px -7px rgb(0 0 0 / 0.15), 0 10px 19px -5px rgb(0 0 0 / 0.12)',\n    '0 31px 47px -8px rgb(0 0 0 / 0.16), 0 12px 23px -6px rgb(0 0 0 / 0.13)',\n    '0 36px 54px -9px rgb(0 0 0 / 0.17), 0 14px 27px -7px rgb(0 0 0 / 0.14)',\n    '0 42px 62px -10px rgb(0 0 0 / 0.18), 0 16px 31px -8px rgb(0 0 0 / 0.15)',\n    '0 48px 70px -11px rgb(0 0 0 / 0.2), 0 18px 36px -9px rgb(0 0 0 / 0.16)',\n    '0 54px 78px -12px rgb(0 0 0 / 0.21), 0 20px 41px -10px rgb(0 0 0 / 0.17)',\n    '0 60px 86px -13px rgb(0 0 0 / 0.22), 0 23px 46px -11px rgb(0 0 0 / 0.18)',\n    '0 66px 94px -14px rgb(0 0 0 / 0.23), 0 26px 52px -12px rgb(0 0 0 / 0.19)',\n    '0 72px 102px -15px rgb(0 0 0 / 0.24), 0 29px 58px -13px rgb(0 0 0 / 0.2)',\n    '0 58px 82px -11px rgb(0 0 0 / 0.26), 0 21px 40px -11px rgb(0 0 0 / 0.22)',\n  ],\n};\n\nconst brandedComponents: ThemeOptions['components'] = {\n  MuiButton: {\n    defaultProps: {\n      disableElevation: true,\n    },\n    styleOverrides: {\n      root: ({ theme }) => ({\n        minWidth: 'unset',\n        textTransform: 'capitalize',\n        fontSize: '1rem',\n        '&:hover': {\n          textDecoration: 'underline',\n        },\n        [theme.breakpoints.up('md')]: {\n          fontSize: '0.875rem',\n        },\n      }),\n    },\n  },\n};\n\nconst brandedTheme = createTheme({\n  ...brandedTokens,\n  components: brandedComponents,\n});\n\n/**\n * Application theme\n */\nconst appTheme = createTheme({\n  ...brandedTokens,\n  palette: {\n    ...brandedTokens.palette,\n    primary: {\n      main: '#1976d2',\n    },\n  },\n  components: {\n    ...brandedComponents,\n    MuiButton: {\n      styleOverrides: {\n        root: [\n          brandedComponents?.MuiButton?.styleOverrides?.root,\n          {\n            transition: 'transform 0.2s ease-in-out',\n            '&:hover': {\n              transform: 'translateY(-2px)',\n            },\n          },\n        ],\n      },\n    },\n  },\n});\n\nfunction App1() {\n  return (\n    <ThemeProvider theme={appTheme}>\n      <Button>App Button</Button>\n    </ThemeProvider>\n  );\n}\n\nconst appTheme2 = createTheme({\n  ...brandedTokens,\n  palette: {\n    ...brandedTokens.palette,\n    primary: {\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "      main: '#ffa726',\n    },\n  },\n  components: {\n    ...brandedComponents,\n    MuiButton: {\n      defaultProps: {\n        ...brandedComponents?.MuiButton?.defaultProps,\n        variant: 'outlined',\n      },\n      styleOverrides: {\n        root: [\n          brandedComponents?.MuiButton?.styleOverrides?.root,\n          ({ theme }) => ({\n            color: theme.palette.primary.dark,\n          }),\n        ],\n      },\n    },\n  },\n});\n\nfunction App2() {\n  return (\n    <ThemeProvider theme={appTheme2}>\n      <Button>App 2 Button</Button>\n    </ThemeProvider>\n  );\n}\n\nexport default function ExtensibleThemes() {\n  return (\n    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "dataFrameType": "focus",
            "dataFrameIndent": 3,
            "dataFrameFocusTarget": "",
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "      <ThemeProvider theme={brandedTheme}>\n        <Button>Branded Button</Button>\n      </ThemeProvider>\n      <App1 />\n      <App2 />\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "    </Box>\n  );\n}\n"
            }
          ]
        }
      ],
      "data": {
        "totalLines": 161,
        "focusedLines": 5,
        "collapsible": true
      }
    },
    "fallback": [
      [
        "span",
        "frame",
        "import Box from '@mui/material/Box';\nimport Button from '@mui/material/Button';\nimport { ThemeProvider, createTheme, type ThemeOptions } from '@mui/material/styles';\n\n/**\n * Branded theme: you might want to export this as a separate file\n */\nconst brandedTokens: ThemeOptions = {\n  palette: {\n    primary: {\n      main: '#000000',\n    },\n    secondary: {\n      main: 'rgb(229, 229, 234)',\n    },\n  },\n  shape: {\n    borderRadius: 4,\n  },\n  typography: {\n    fontFamily:\n      'var(--font-primary, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif)',\n  },\n  shadows: [\n    'none',\n    '0 1px 2px 0 rgb(0 0 0 / 0.05)',\n    '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',\n    '0 2px 4px 0 rgb(0 0 0 / 0.06)',\n    '0 2px 4px -1px rgb(0 0 0 / 0.06), 0 1px 2px -1px rgb(0 0 0 / 0.04)',\n    '0 3px 5px -1px rgb(0 0 0 / 0.07), 0 1px 3px -1px rgb(0 0 0 / 0.05)',\n    '0 4px 6px -1px rgb(0 0 0 / 0.07), 0 2px 4px -1px rgb(0 0 0 / 0.05)',\n    '0 5px 8px -2px rgb(0 0 0 / 0.08), 0 2px 4px -1px rgb(0 0 0 / 0.05)',\n    '0 6px 10px -2px rgb(0 0 0 / 0.08), 0 3px 5px -2px rgb(0 0 0 / 0.06)',\n    '0 8px 12px -3px rgb(0 0 0 / 0.09), 0 3px 6px -2px rgb(0 0 0 / 0.06)',\n    '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 7px -3px rgb(0 0 0 / 0.07)',\n    '0 12px 18px -4px rgb(0 0 0 / 0.11), 0 5px 9px -3px rgb(0 0 0 / 0.08)',\n    '0 15px 22px -4px rgb(0 0 0 / 0.12), 0 6px 11px -4px rgb(0 0 0 / 0.09)',\n    '0 18px 28px -5px rgb(0 0 0 / 0.13), 0 7px 13px -4px rgb(0 0 0 / 0.1)',\n    '0 22px 34px -6px rgb(0 0 0 / 0.14), 0 8px 16px -5px rgb(0 0 0 / 0.11)',\n    '0 26px 40px -7px rgb(0 0 0 / 0.15), 0 10px 19px -5px rgb(0 0 0 / 0.12)',\n    '0 31px 47px -8px rgb(0 0 0 / 0.16), 0 12px 23px -6px rgb(0 0 0 / 0.13)',\n    '0 36px 54px -9px rgb(0 0 0 / 0.17), 0 14px 27px -7px rgb(0 0 0 / 0.14)',\n    '0 42px 62px -10px rgb(0 0 0 / 0.18), 0 16px 31px -8px rgb(0 0 0 / 0.15)',\n    '0 48px 70px -11px rgb(0 0 0 / 0.2), 0 18px 36px -9px rgb(0 0 0 / 0.16)',\n    '0 54px 78px -12px rgb(0 0 0 / 0.21), 0 20px 41px -10px rgb(0 0 0 / 0.17)',\n    '0 60px 86px -13px rgb(0 0 0 / 0.22), 0 23px 46px -11px rgb(0 0 0 / 0.18)',\n    '0 66px 94px -14px rgb(0 0 0 / 0.23), 0 26px 52px -12px rgb(0 0 0 / 0.19)',\n    '0 72px 102px -15px rgb(0 0 0 / 0.24), 0 29px 58px -13px rgb(0 0 0 / 0.2)',\n    '0 58px 82px -11px rgb(0 0 0 / 0.26), 0 21px 40px -11px rgb(0 0 0 / 0.22)',\n  ],\n};\n\nconst brandedComponents: ThemeOptions['components'] = {\n  MuiButton: {\n    defaultProps: {\n      disableElevation: true,\n    },\n    styleOverrides: {\n      root: ({ theme }) => ({\n        minWidth: 'unset',\n        textTransform: 'capitalize',\n        fontSize: '1rem',\n        '&:hover': {\n          textDecoration: 'underline',\n        },\n        [theme.breakpoints.up('md')]: {\n          fontSize: '0.875rem',\n        },\n      }),\n    },\n  },\n};\n\nconst brandedTheme = createTheme({\n  ...brandedTokens,\n  components: brandedComponents,\n});\n\n/**\n * Application theme\n */\nconst appTheme = createTheme({\n  ...brandedTokens,\n  palette: {\n    ...brandedTokens.palette,\n    primary: {\n      main: '#1976d2',\n    },\n  },\n  components: {\n    ...brandedComponents,\n    MuiButton: {\n      styleOverrides: {\n        root: [\n          brandedComponents?.MuiButton?.styleOverrides?.root,\n          {\n            transition: 'transform 0.2s ease-in-out',\n            '&:hover': {\n              transform: 'translateY(-2px)',\n            },\n          },\n        ],\n      },\n    },\n  },\n});\n\nfunction App1() {\n  return (\n    <ThemeProvider theme={appTheme}>\n      <Button>App Button</Button>\n    </ThemeProvider>\n  );\n}\n\nconst appTheme2 = createTheme({\n  ...brandedTokens,\n  palette: {\n    ...brandedTokens.palette,\n    primary: {\n"
      ],
      [
        "span",
        "frame",
        "      main: '#ffa726',\n    },\n  },\n  components: {\n    ...brandedComponents,\n    MuiButton: {\n      defaultProps: {\n        ...brandedComponents?.MuiButton?.defaultProps,\n        variant: 'outlined',\n      },\n      styleOverrides: {\n        root: [\n          brandedComponents?.MuiButton?.styleOverrides?.root,\n          ({ theme }) => ({\n            color: theme.palette.primary.dark,\n          }),\n        ],\n      },\n    },\n  },\n});\n\nfunction App2() {\n  return (\n    <ThemeProvider theme={appTheme2}>\n      <Button>App 2 Button</Button>\n    </ThemeProvider>\n  );\n}\n\nexport default function ExtensibleThemes() {\n  return (\n    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>\n"
      ],
      [
        "span",
        "frame",
        {
          "dataFrameType": "focus",
          "dataFrameIndent": 3,
          "dataFrameFocusTarget": ""
        },
        "      <ThemeProvider theme={brandedTheme}>\n        <Button>Branded Button</Button>\n      </ThemeProvider>\n      <App1 />\n      <App2 />\n"
      ],
      [
        "span",
        "frame",
        "    </Box>\n  );\n}\n"
      ]
    ],
    "fallbackCritical": {
      "2": [
        "span",
        "frame",
        {
          "dataFrameType": "focus",
          "dataFrameIndent": 3,
          "dataFrameFocusTarget": ""
        },
        [
          [
            "span",
            "line",
            {
              "dataLn": 154
            },
            [
              "      <",
              [
                "span",
                "pl-c1 di-jsx",
                "ThemeProvider"
              ],
              " ",
              [
                "span",
                "pl-e di-ak",
                "theme"
              ],
              [
                "span",
                "pl-k di-pu di-ae",
                "="
              ],
              [
                "span",
                "pl-pse",
                "{"
              ],
              [
                "span",
                "pl-smi di-jv",
                "brandedTheme"
              ],
              [
                "span",
                "pl-pse",
                "}"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 155
            },
            [
              "        <",
              [
                "span",
                "pl-c1 di-jsx",
                "Button"
              ],
              ">Branded Button</",
              [
                "span",
                "pl-c1 di-jsx",
                "Button"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 156
            },
            [
              "      </",
              [
                "span",
                "pl-c1 di-jsx",
                "ThemeProvider"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 157
            },
            [
              "      <",
              [
                "span",
                "pl-c1 di-jsx",
                "App1"
              ],
              " />"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 158
            },
            [
              "      <",
              [
                "span",
                "pl-c1 di-jsx",
                "App2"
              ],
              " />"
            ]
          ],
          "\n"
        ]
      ]
    },
    "totalLines": 161,
    "focusedLines": 5,
    "collapsible": true,
    "externals": [
      "@mui/material/Box",
      "@mui/material/Button",
      "@mui/material/styles"
    ]
  }
}, loadPrecompute: () => import("./index.ts?mui-deferred-precompute").then((module) => module.default) });
